import React from "react";
import { TrashIcon, PlusIcon, Bars3Icon } from "../../../../automationStudioIcons";
import SmartInputBox from "../../../shared/SmartInputBox";
import ButtonComponent from "../../../../../customButton";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelectorsTab } from "../../../../../AutomationStudio/components/RightPanel/StepEditorView/tabs/useSelectorsTab";
import styles from "../../../../../../styles/automationStudio/automationStudio.module.scss";
import { SELECTOR_TYPE_MAP, isReplayDeletableType } from "./selectorsTab.constants";
import { toolSupportsElements } from "../../../../../AutomationStudio/constant";

const SortableSelectorCard = React.memo(
    ({
        selector,
        index,
        dragDisabled,
        onChange,
        onBlur,
        onDelete,
        variableSuggestions,
        variableDescriptions,
        fileSuggestions,
        fileDescriptions,
        isAddingCustom,
        onSavePending,
        onCancelPending,
        pendingSaveDisabled,
        actionLocked,
        isCardDirty = false,
    }) => {
        const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
            id: String(selector.uid),
            disabled: dragDisabled,
        });
        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
        };

        const meta = SELECTOR_TYPE_MAP[selector.type];
        const typeLabel = meta?.label ?? selector.type;
        const badgeTone = meta?.badgeTone ?? "neutral";
        const canDelete = isReplayDeletableType(selector.type) && !isAddingCustom;

        const typeBadgeClass =
            {
                primary: styles.selectorTypeBadgePrimary,
                secondary: styles.selectorTypeBadgeSecondary,
                neutral: styles.selectorTypeBadgeNeutral,
            }[badgeTone] ?? styles.selectorTypeBadgeNeutral;

        const isPending = Boolean(selector.isPending);

        const selectorInput = (
            <SmartInputBox
                size="small"
                value={selector.value}
                onChange={(e) => onChange(index, e.target.value)}
                onBlur={(e) => onBlur(index, e.target.value)}
                placeholder="XPath or CSS — type ${ for variables, @ for files"
                suggestions={variableSuggestions}
                descriptions={variableDescriptions}
                fileTrigger="@"
                fileSuggestions={fileSuggestions}
                fileDescriptions={fileDescriptions}
                formatFileInsertion={(file) => `@[${file.id}]`}
                disabled={actionLocked}
                fullWidth
                data-selector-input={selector.uid}
                customClassName={styles.selectorsFieldInput}
            />
        );

        return (
            <div
                ref={setNodeRef}
                style={style}
                className={`${styles.selectorCard} ${isPending ? styles.selectorCardEditing : ""}`}
            >
                <div className={styles.selectorHandle}>
                    <span
                        {...(dragDisabled ? {} : { ...attributes, ...listeners })}
                        style={{
                            display: "inline-flex",
                            cursor: dragDisabled ? "not-allowed" : "grab",
                            opacity: dragDisabled ? 0.6 : 1,
                        }}
                    >
                        <Bars3Icon
                            className={styles.dragIcon}
                            title={dragDisabled ? "Finish or cancel adding a selector to reorder" : "Drag to reorder"}
                        />
                    </span>
                    <span className={`${styles.selectorNumber} ${styles.asTypoCaption}`}>{index + 1}</span>
                </div>

                <div className={styles.selectorContent}>
                    <span className={`${styles.selectorTypeBadge} ${typeBadgeClass}`}>{typeLabel}</span>
                    {isCardDirty
                        ? <div className={styles.dirtyFieldTarget}>{selectorInput}</div>
                        : selectorInput}
                    {isPending ? (
                        <div className={styles.selectorCardPendingActions}>
                            <ButtonComponent
                                ctaText="Save"
                                variant="primary"
                                size="small"
                                className="white bg-blue600"
                                onClick={onSavePending}
                                disabled={pendingSaveDisabled || actionLocked}
                            />
                            <ButtonComponent
                                ctaText="Cancel"
                                variant="outline"
                                size="small"
                                className="primary"
                                onClick={onCancelPending}
                            />
                        </div>
                    ) : null}
                </div>

                {canDelete ? (
                    <div className={styles.selectorActions}>
                        <button
                            type="button"
                            onClick={() => onDelete(index)}
                            title="Delete selector"
                            className={`${styles.asIconButton} ${styles.selectorActionButton}`}
                            disabled={actionLocked}
                            aria-disabled={actionLocked}
                        >
                            <TrashIcon style={{ width: "14px", height: "14px" }} />
                        </button>
                    </div>
                ) : null}
            </div>
        );
    }
);

SortableSelectorCard.displayName = "SortableSelectorCard";

/**
 * Selectors tab — wires useSelectorsTab into the presentational UI.
 */
export default function SelectorsTab({
    step,
    onStepChange,
    isExecuting = false,
    readOnlyForMetaInfo = false,
    fileSuggestions = [],
    fileDescriptions = {},
    variablesList = [],
    variablesListDescriptions = {},
    embedded = false,
    strategyDirtyById = null,
}) {
    if (!toolSupportsElements(step?.tool)) {
        return null;
    }

    const {
        sensors,
        orderedSelectors,
        isAddingCustom,
        handleSelectorChange,
        handleSelectorBlur,
        handleDeleteSelector,
        handleAddCustomSelector,
        handleSaveCustomSelector,
        handleCancelCustomSelector,
        handleDragEnd,
        showList,
        dragDisabled,
        pendingRowHasValue,
        hasExecutionStrategies,
    } = useSelectorsTab({ step, onStepChange, isExecuting, readOnlyForMetaInfo });

    const actionLocked = isExecuting || readOnlyForMetaInfo;

    return (
        <div className={`${styles.selectorsTabContainer} ${embedded ? styles.selectorsTabEmbedded : ""}`}>
            <div className={styles.selectorsHeader}>
                <span className={`${styles.sectionLabel} ${styles.asTypoSubtitle2}`}>Fallback Selector Priority</span>
                <span className={`${styles.helperText} ${styles.asTypoCaption}`}>
                    Drag to reorder. Executes top-to-bottom until one succeeds.
                </span>
            </div>

            <div className={styles.selectorsList}>
                {!showList ? (
                    <div className={styles.emptyState}>
                        <p className={`${styles.asTypoBody2} ${styles.asTextSecondary}`}>No XPath selectors in this list</p>
                        <p className={`${styles.asTypoCaption} ${styles.asTextSecondary}`}>
                            Only strategies of type custom, targeted (Playwright), resilient (rules), or dynamic are shown.
                            {hasExecutionStrategies
                                ? " Other strategy types (e.g. Playwright code) are not listed here."
                                : " Add execution strategies or use Add Custom Selector."}
                        </p>
                    </div>
                ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext
                            items={orderedSelectors.map((s) => String(s.uid))}
                            strategy={verticalListSortingStrategy}
                        >
                            {orderedSelectors.map((selector, index) => (
                                <SortableSelectorCard
                                    key={String(selector.uid)}
                                    selector={selector}
                                    index={index}
                                    dragDisabled={dragDisabled}
                                    actionLocked={actionLocked}
                                    variableSuggestions={variablesList}
                                    variableDescriptions={variablesListDescriptions}
                                    fileSuggestions={fileSuggestions}
                                    fileDescriptions={fileDescriptions}
                                    onChange={handleSelectorChange}
                                    onBlur={handleSelectorBlur}
                                    onDelete={handleDeleteSelector}
                                    isAddingCustom={isAddingCustom}
                                    onSavePending={handleSaveCustomSelector}
                                    onCancelPending={handleCancelCustomSelector}
                                    pendingSaveDisabled={!pendingRowHasValue}
                                    isCardDirty={Boolean(
                                        selector.strategyId != null &&
                                        strategyDirtyById?.[String(selector.strategyId)]
                                    )}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                )}
            </div>

            {!actionLocked ? (
                <ButtonComponent
                    ctaText="Add Custom Selector"
                    variant="outline"
                    size="small"
                    startIcon={<PlusIcon className="icon" style={{ width: 16, height: 16 }} />}
                    onClick={handleAddCustomSelector}
                    disabled={isAddingCustom}
                />
            ) : null}

            <div className={styles.selectorsHelp}>
                <p className={`${styles.asTypoCaption} ${styles.asTextSecondary}`}>
                    💡 <strong>Playwright</strong>: AI-generated Playwright selectors<br />
                    💡 <strong>Custom</strong>: Your manually added selectors
                </p>
            </div>
        </div>
    );
}
